// Service Worker Registration Utility - Optimized for Safari/iOS

// Check if service worker is supported
const isSupported = (): boolean => {
  return 'serviceWorker' in navigator && 'caches' in window;
};

// Check if running on iOS Safari
const isIOSSafari = (): boolean => {
  const userAgent = navigator.userAgent;
  return /iPad|iPhone|iPod/.test(userAgent) && /Safari/.test(userAgent) && !/CriOS|FxiOS|OPiOS|mercury/.test(userAgent);
};

// Check if running on Safari
const isSafari = (): boolean => {
  const userAgent = navigator.userAgent;
  return /Safari/.test(userAgent) && !/Chrome|CriOS|FxiOS|OPiOS|mercury/.test(userAgent);
};

// Register service worker
export const register = async (): Promise<ServiceWorkerRegistration | null> => {
  if (!isSupported()) {
    console.log('Service Worker not supported');
    return null;
  }

  try {
    const registration = await navigator.serviceWorker.register('/sw.js', {
      scope: '/',
      updateViaCache: 'none' // Always check for updates
    });

    console.log('Service Worker registered successfully:', registration);

    // Handle service worker updates
    registration.addEventListener('updatefound', () => {
      const newWorker = registration.installing;
      if (newWorker) {
        console.log('Service Worker update found');
        
        newWorker.addEventListener('statechange', () => {
          if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
            console.log('New service worker installed, waiting for activation');
            // Show update notification to user
            showUpdateNotification();
          }
        });
      }
    });

    // Handle controller change
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      console.log('Service Worker controller changed');
      // Reload page to use new service worker
      if (isIOSSafari() || isSafari()) {
        // Safari sometimes needs a manual reload
        window.location.reload();
      }
    });

    return registration;
  } catch (error) {
    console.error('Service Worker registration failed:', error);
    return null;
  }
};

// Unregister service worker
export const unregister = async (): Promise<boolean> => {
  if (!isSupported()) {
    return false;
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    const result = await registration.unregister();
    console.log('Service Worker unregistered:', result);
    return result;
  } catch (error) {
    console.error('Service Worker unregistration failed:', error);
    return false;
  }
};

// Check for updates
export const checkForUpdates = async (): Promise<void> => {
  if (!isSupported()) {
    return;
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    await registration.update();
    console.log('Service Worker update check completed');
  } catch (error) {
    console.error('Service Worker update check failed:', error);
  }
};

// Skip waiting (activate new service worker immediately)
export const skipWaiting = async (): Promise<void> => {
  if (!isSupported()) {
    return;
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    if (registration.waiting) {
      registration.waiting.postMessage({ type: 'SKIP_WAITING' });
      console.log('Skip waiting message sent');
    }
  } catch (error) {
    console.error('Skip waiting failed:', error);
  }
};

// Get service worker version
export const getVersion = async (): Promise<string | null> => {
  if (!isSupported()) {
    return null;
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    if (registration.active) {
      return new Promise((resolve) => {
        const channel = new MessageChannel();
        channel.port1.onmessage = (event) => {
          resolve(event.data.version);
        };
        registration.active!.postMessage({ type: 'GET_VERSION' }, [channel.port2]);
      });
    }
  } catch (error) {
    console.error('Get version failed:', error);
  }
  return null;
};

// Show update notification
const showUpdateNotification = (): void => {
  // Create a simple update notification
  const notification = document.createElement('div');
  notification.style.cssText = `
    position: fixed;
    top: 20px;
    right: 20px;
    background: #007bff;
    color: white;
    padding: 15px 20px;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
    z-index: 10000;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    font-size: 14px;
    max-width: 300px;
    animation: slideIn 0.3s ease-out;
  `;
  
  notification.innerHTML = `
    <div style="display: flex; align-items: center; gap: 10px;">
      <span>🔄 New version available</span>
      <button onclick="this.parentElement.parentElement.remove(); window.location.reload();" 
              style="background: white; color: #007bff; border: none; padding: 5px 10px; border-radius: 4px; cursor: pointer; font-size: 12px;">
        Update
      </button>
      <button onclick="this.parentElement.parentElement.remove();" 
              style="background: transparent; color: white; border: 1px solid white; padding: 5px 10px; border-radius: 4px; cursor: pointer; font-size: 12px;">
        Later
      </button>
    </div>
  `;
  
  // Add CSS animation
  const style = document.createElement('style');
  style.textContent = `
    @keyframes slideIn {
      from { transform: translateX(100%); opacity: 0; }
      to { transform: translateX(0); opacity: 1; }
    }
  `;
  document.head.appendChild(style);
  
  document.body.appendChild(notification);
  
  // Auto-remove after 10 seconds
  setTimeout(() => {
    if (notification.parentNode) {
      notification.remove();
    }
  }, 10000);
};

// Initialize service worker with iOS/Safari optimizations
export const initializeServiceWorker = async (): Promise<void> => {
  if (!isSupported()) {
    console.log('Service Worker not supported in this browser');
    return;
  }

  // iOS Safari specific optimizations
  if (isIOSSafari()) {
    console.log('Detected iOS Safari - applying optimizations');
    
    // Force HTTPS check for iOS
    if (location.protocol !== 'https:' && location.hostname !== 'localhost') {
      console.warn('Service Worker requires HTTPS on iOS Safari');
      return;
    }
  }

  // Safari specific optimizations
  if (isSafari()) {
    console.log('Detected Safari - applying optimizations');
  }

  try {
    const registration = await register();
    
    if (registration) {
      // Set up periodic update checks (every 6 hours)
      setInterval(checkForUpdates, 6 * 60 * 60 * 1000);
      
      // Check for updates on page focus (useful for mobile)
      document.addEventListener('visibilitychange', () => {
        if (!document.hidden) {
          checkForUpdates();
        }
      });
      
      console.log('Service Worker initialized successfully');
    }
  } catch (error) {
    console.error('Service Worker initialization failed:', error);
  }
};

// Export utility functions
export default {
  register,
  unregister,
  checkForUpdates,
  skipWaiting,
  getVersion,
  initializeServiceWorker,
  isSupported,
  isIOSSafari,
  isSafari
}; 